// @ts-nocheck
/**
 * Dashboard Customization Service
 * Handles user preferences, layout persistence, and role-based configurations
 */

class DashboardCustomizationService {
  constructor() {
    this.storageKey = 'dashboard_customization';
    this.behaviorKey = 'dashboard_behavior';
    this.defaultConfigs = this.getDefaultConfigurations();
  }

  /**
   * Get default dashboard configurations for different roles
   */
  getDefaultConfigurations() {
    return {
      marketer: {
        widgets: [
          { id: 'kpi-widgets', type: 'kpi', enabled: true, order: 1, size: 'full', config: {} },
          { id: 'attention-widget', type: 'attention', enabled: true, order: 2, size: 'half', config: {} },
          { id: 'quick-actions', type: 'quickActions', enabled: true, order: 3, size: 'half', config: {} },
          { id: 'performance-widget', type: 'performance', enabled: true, order: 4, size: 'full', config: { defaultTimeRange: '30d', defaultMetrics: ['openRates', 'clickRates'] } },
          { id: 'top-performers', type: 'topPerformers', enabled: true, order: 5, size: 'half', config: { defaultTab: 'campaigns' } },
          { id: 'team-activity', type: 'teamActivity', enabled: true, order: 6, size: 'half', config: { showNotifications: true } }
        ],
        preferences: {
          defaultTimeRange: '30d',
          autoRefresh: true,
          refreshInterval: 300000,
          theme: 'light',
          compactMode: false
        }
      },
      analyst: {
        widgets: [
          { id: 'kpi-widgets', type: 'kpi', enabled: true, order: 1, size: 'full', config: {} },
          { id: 'performance-widget', type: 'performance', enabled: true, order: 2, size: 'full', config: { defaultTimeRange: '90d', defaultMetrics: ['openRates', 'clickRates', 'conversionRates', 'revenue'] } },
          { id: 'top-performers', type: 'topPerformers', enabled: true, order: 3, size: 'full', config: { defaultTab: 'segments' } },
          { id: 'attention-widget', type: 'attention', enabled: true, order: 4, size: 'half', config: {} },
          { id: 'team-activity', type: 'teamActivity', enabled: true, order: 5, size: 'half', config: { showNotifications: false } },
          { id: 'quick-actions', type: 'quickActions', enabled: false, order: 6, size: 'third', config: {} }
        ],
        preferences: {
          defaultTimeRange: '90d',
          autoRefresh: true,
          refreshInterval: 180000,
          theme: 'light',
          compactMode: true
        }
      },
      manager: {
        widgets: [
          { id: 'kpi-widgets', type: 'kpi', enabled: true, order: 1, size: 'full', config: {} },
          { id: 'team-activity', type: 'teamActivity', enabled: true, order: 2, size: 'half', config: { showNotifications: true, showTeamMetrics: true } },
          { id: 'attention-widget', type: 'attention', enabled: true, order: 3, size: 'half', config: {} },
          { id: 'performance-widget', type: 'performance', enabled: true, order: 4, size: 'full', config: { defaultTimeRange: '30d', defaultMetrics: ['revenue', 'conversionRates'] } },
          { id: 'top-performers', type: 'topPerformers', enabled: true, order: 5, size: 'half', config: { defaultTab: 'campaigns' } },
          { id: 'quick-actions', type: 'quickActions', enabled: true, order: 6, size: 'half', config: {} }
        ],
        preferences: {
          defaultTimeRange: '30d',
          autoRefresh: true,
          refreshInterval: 300000,
          theme: 'light',
          compactMode: false
        }
      }
    };
  }

  /**
   * Load user's dashboard configuration
   */
  async loadConfiguration(userId, role) {
    try {
      // Try to load from localStorage first
      const stored = localStorage.getItem(`${this.storageKey}_${userId}`);
      
      if (stored) {
        const config = JSON.parse(stored);
        
        // If role has changed, merge with new role defaults
        if (config.role !== role) {
          return this.mergeWithRoleDefaults(config, role);
        }
        
        return config;
      }
      
      // Return default configuration for role
      return this.getDefaultConfigurationForRole(userId, role);
    } catch (error) {
      console.error('Error loading dashboard configuration:', error);
      return this.getDefaultConfigurationForRole(userId, role);
    }
  }

  /**
   * Save user's dashboard configuration
   */
  async saveConfiguration(config) {
    try {
      const configToSave = {
        ...config,
        lastModified: new Date().toISOString()
      };
      
      localStorage.setItem(`${this.storageKey}_${config.userId}`, JSON.stringify(configToSave));
      
      // In a real app, this would also save to the backend
      // await this.saveToBackend(configToSave);
      
      return true;
    } catch (error) {
      console.error('Error saving dashboard configuration:', error);
      return false;
    }
  }

  /**
   * Get default configuration for a specific role
   */
  getDefaultConfigurationForRole(userId, role) {
    const roleConfig = this.defaultConfigs[role] || this.defaultConfigs.marketer;
    
    return {
      userId,
      role,
      layout: {
        widgets: [...roleConfig.widgets],
        gridColumns: 12,
        customizations: {}
      },
      preferences: { ...roleConfig.preferences },
      behaviorData: {
        widgetInteractions: {},
        timeSpentOnWidgets: {},
        preferredActions: [],
        lastActiveWidgets: []
      },
      lastModified: new Date().toISOString()
    };
  }

  /**
   * Merge existing config with new role defaults
   */
  mergeWithRoleDefaults(existingConfig, newRole) {
    const roleDefaults = this.defaultConfigs[newRole] || this.defaultConfigs.marketer;
    
    // Keep user's customizations but update role-specific defaults
    const mergedWidgets = roleDefaults.widgets.map(defaultWidget => {
      const existingWidget = existingConfig.layout.widgets.find(w => w.id === defaultWidget.id);
      
      if (existingWidget) {
        // Keep user's enabled/order preferences but update role-specific config
        return {
          ...defaultWidget,
          enabled: existingWidget.enabled,
          order: existingWidget.order,
          size: existingWidget.size,
          config: { ...defaultWidget.config, ...existingWidget.config }
        };
      }
      
      return defaultWidget;
    });

    return {
      ...existingConfig,
      role: newRole,
      layout: {
        ...existingConfig.layout,
        widgets: mergedWidgets
      },
      preferences: {
        ...roleDefaults.preferences,
        ...existingConfig.preferences
      }
    };
  }

  /**
   * Reset configuration to role defaults
   */
  async resetToDefaults(userId, role) {
    const defaultConfig = this.getDefaultConfigurationForRole(userId, role);
    await this.saveConfiguration(defaultConfig);
    return defaultConfig;
  }

  /**
   * Update widget order
   */
  async updateWidgetOrder(config, widgetId, newOrder) {
    const updatedWidgets = config.layout.widgets.map(widget => {
      if (widget.id === widgetId) {
        return { ...widget, order: newOrder };
      }
      return widget;
    });

    // Re-sort and normalize orders
    const sortedWidgets = updatedWidgets.sort((a, b) => a.order - b.order);
    const normalizedWidgets = sortedWidgets.map((widget, index) => ({
      ...widget,
      order: index + 1
    }));

    const updatedConfig = {
      ...config,
      layout: {
        ...config.layout,
        widgets: normalizedWidgets
      }
    };

    await this.saveConfiguration(updatedConfig);
    return updatedConfig;
  }

  /**
   * Toggle widget visibility
   */
  async toggleWidgetVisibility(config, widgetId) {
    const updatedWidgets = config.layout.widgets.map(widget =>
      widget.id === widgetId 
        ? { ...widget, enabled: !widget.enabled }
        : widget
    );

    const updatedConfig = {
      ...config,
      layout: {
        ...config.layout,
        widgets: updatedWidgets
      }
    };

    await this.saveConfiguration(updatedConfig);
    return updatedConfig;
  }

  /**
   * Update widget configuration
   */
  async updateWidgetConfig(config, widgetId, widgetConfig) {
    const updatedWidgets = config.layout.widgets.map(widget =>
      widget.id === widgetId 
        ? { ...widget, config: { ...widget.config, ...widgetConfig } }
        : widget
    );

    const updatedConfig = {
      ...config,
      layout: {
        ...config.layout,
        widgets: updatedWidgets
      }
    };

    await this.saveConfiguration(updatedConfig);
    return updatedConfig;
  }

  /**
   * Update widget size
   */
  async updateWidgetSize(config, widgetId, newSize) {
    const updatedWidgets = config.layout.widgets.map(widget =>
      widget.id === widgetId 
        ? { ...widget, size: newSize }
        : widget
    );

    const updatedConfig = {
      ...config,
      layout: {
        ...config.layout,
        widgets: updatedWidgets
      }
    };

    await this.saveConfiguration(updatedConfig);
    return updatedConfig;
  }

  /**
   * Track user behavior for learning
   */
  async trackBehavior(userId, action, data) {
    try {
      const behaviorKey = `${this.behaviorKey}_${userId}`;
      const existing = localStorage.getItem(behaviorKey);
      const behaviorData = existing ? JSON.parse(existing) : {
        widgetInteractions: {},
        timeSpentOnWidgets: {},
        preferredActions: [],
        lastActiveWidgets: [],
        actionHistory: []
      };

      // Track the action
      behaviorData.actionHistory.push({
        action,
        data,
        timestamp: new Date().toISOString()
      });

      // Keep only last 1000 actions
      if (behaviorData.actionHistory.length > 1000) {
        behaviorData.actionHistory = behaviorData.actionHistory.slice(-1000);
      }

      // Update specific behavior tracking
      switch (action) {
        case 'widget_interaction':
          behaviorData.widgetInteractions[data.widgetId] = 
            (behaviorData.widgetInteractions[data.widgetId] || 0) + 1;
          break;
        
        case 'widget_time_spent':
          behaviorData.timeSpentOnWidgets[data.widgetId] = 
            (behaviorData.timeSpentOnWidgets[data.widgetId] || 0) + data.timeSpent;
          break;
        
        case 'quick_action_used':
          const actionIndex = behaviorData.preferredActions.findIndex(a => a.action === data.action);
          if (actionIndex >= 0) {
            behaviorData.preferredActions[actionIndex].count++;
          } else {
            behaviorData.preferredActions.push({ action: data.action, count: 1 });
          }
          break;
      }

      localStorage.setItem(behaviorKey, JSON.stringify(behaviorData));
    } catch (error) {
      console.error('Error tracking behavior:', error);
    }
  }

  /**
   * Get behavior-based recommendations
   */
  getBehaviorRecommendations(userId, currentConfig) {
    try {
      const behaviorKey = `${this.behaviorKey}_${userId}`;
      const behaviorData = localStorage.getItem(behaviorKey);
      
      if (!behaviorData) return [];
      
      const data = JSON.parse(behaviorData);
      const recommendations = [];

      // Recommend enabling frequently interacted widgets
      Object.entries(data.widgetInteractions).forEach(([widgetId, count]) => {
        const widget = currentConfig.layout.widgets.find(w => w.id === widgetId);
        if (widget && !widget.enabled && count > 10) {
          recommendations.push({
            type: 'enable_widget',
            widgetId,
            reason: `You've interacted with this widget ${count} times`,
            priority: 'medium'
          });
        }
      });

      // Recommend reordering based on time spent
      const timeSpentEntries = Object.entries(data.timeSpentOnWidgets)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3);

      timeSpentEntries.forEach(([widgetId, timeSpent], index) => {
        const widget = currentConfig.layout.widgets.find(w => w.id === widgetId);
        if (widget && widget.enabled && widget.order > index + 2) {
          recommendations.push({
            type: 'reorder_widget',
            widgetId,
            suggestedOrder: index + 2,
            reason: `You spend a lot of time on this widget`,
            priority: 'low'
          });
        }
      });

      return recommendations;
    } catch (error) {
      console.error('Error getting behavior recommendations:', error);
      return [];
    }
  }

  /**
   * Export configuration
   */
  exportConfiguration(config) {
    const exportData = {
      ...config,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard-config-${config.userId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Import configuration
   */
  async importConfiguration(file, userId) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const importedConfig = JSON.parse(e.target.result);
          
          // Validate the configuration
          if (!this.validateConfiguration(importedConfig)) {
            reject(new Error('Invalid configuration file'));
            return;
          }
          
          // Update user ID and save
          const configToSave = {
            ...importedConfig,
            userId,
            lastModified: new Date().toISOString()
          };
          
          await this.saveConfiguration(configToSave);
          resolve(configToSave);
        } catch (error) {
          reject(new Error('Failed to parse configuration file'));
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Validate configuration structure
   */
  validateConfiguration(config) {
    return !!(
      config &&
      typeof config === 'object' &&
      config.layout &&
      Array.isArray(config.layout.widgets) &&
      config.preferences &&
      typeof config.preferences === 'object'
    );
  }
}

export default new DashboardCustomizationService();